<template>
  <PortalLayout section="Documents">
    <div class="mt-4">
      <div v-if="isPropertyLoading"><Loading /></div>
      <div v-else>
        <div v-if="docs && docs.length > 0">
          <span class="w-full flex my-2" v-if="docOrderChanged">
            <p class="grow text-red-700">
              Your sort order changes are unsaved, when done sorting, please hit the 'Save
              Changes' button to save
            </p>
            <SaveButton @click="saveSortedDocs" />
          </span>
          <AddButton @click="addClick(true)">Add Document</AddButton>
          <div
            v-for="doc in docs"
            :key="doc.documentId"
            class="bg-gray-100 border-t my-2"
          >
            <div class="flex">
              <div class="flex-col mx-2">
                <i-heroicons-outline-chevron-up
                  class="h-5 w-5 mt-1 mb-2"
                  :class="
                    doc.sortOrder == Math.min(...docs.map((d) => d.sortOrder))
                      ? 'text-gray-400'
                      : 'cursor-pointer hover:bg-blue-200'
                  "
                  @click="swapDocSort(doc.sortOrder, true)"
                />
                <i-heroicons-outline-chevron-down
                  class="h-5 w-5"
                  :class="
                    doc.sortOrder == Math.max(...docs.map((d) => d.sortOrder))
                      ? 'text-gray-400'
                      : 'cursor-pointer hover:bg-blue-200'
                  "
                  @click="swapDocSort(doc.sortOrder, false)"
                />
              </div>
              <div
                class="flex-col pl-2 pr-4 py-2 cursor-pointer hover:bg-blue-200 grow h-full"
                @click="editClick(doc)"
              >
                <div class="flex">
                  <div class="flex-col grow">
                    <div>{{ doc.label }}</div>
                    <div class="ml-2 font-mono text-sm text-gray-700">
                      {{ doc.fileName }}
                    </div>
                  </div>
                  <div class="flex-col">{{ formatBytes(doc.fileSize) }}</div>
                </div>
              </div>
            </div>
            <div class="p-2 flex">
              <input
                class="font-mono p-1 px-2 w-full bg-gray-100"
                :value="getDocDownloadPath(doc)"
                readonly
              />
              <a
                target="_blank"
                title="Launch Document in new Window"
                :href="getDocDownloadPathWithToken(doc)"
                class="bg-gray-200 hover:bg-blue-300 px-1 py-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div v-else class="mb-2">
          <span>-- No Documents found for this building --</span>
        </div>
        <div>
          <AddButton @click="addClick(false)">Add Document</AddButton>
        </div>
        <EditPopup
          entity-name="Document"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="saveError"
          :delete-enabled="true"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <p
            v-if="documentFileSize > bldgStore.maxOfflineDocSize"
            class="bg-yellow-200 border-2 border-yellow-300 p-2 mb-8"
          >
            The current file is not available in offline mode due to large size.
          </p>
          <BaseInput
            label="Label"
            v-model="documentLabel"
            :error="errors.documentLabel"
          />
          <div class="flex pt-4">
            <input type="file" class="flex-col grow" @change="fileChange" />
            <div v-if="file != undefined" class="flex-col">
              {{ formatBytes(file?.size ?? 0) }}
            </div>
          </div>
          <p
            v-if="file != null && file.size > bldgStore.maxOfflineDocSize"
            class="bg-yellow-200 border-2 border-yellow-300 mt-2 p-2"
          >
            This file will not be available in offline mode due to large size.
          </p>
          <BaseInput
            label="File Name"
            v-model="documentFileName"
            :error="errors.documentFileName"
          />
          <BaseCheckbox
            :model-value="documentAvailableOffline"
            label="Is Available Offline"
            @update:modelValue="documentAvailableOffline = $event"
            class="mt-2"
          />
          <p class="mt-6 mb-1">Edit Visibility</p>
          <BaseCheckbox
            :model-value="documentAllowAnonLinkView"
            label="Anonymous Link Viewing"
            @update:modelValue="documentAllowAnonLinkView = $event"
          />
          <BaseCheckbox
            :model-value="documentAllowAnonDownload"
            label="Anonymous Downloading"
            @update:modelValue="documentAllowAnonDownload = $event"
          />
          <div class="border-t border-gray-300 m-2 w-1/2" />
          <BaseCheckboxGroup
            :model-value="documentSelectedRoleOptions"
            :options="documentRoleOptions"
            label=""
            vertical
          />
        </EditPopup>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useUserStore } from "../../store/userStore";
import { useBldgStore } from "../../store/bldgStore";
import { DocumentDTO } from "../../store/generatedFetchClient";
import { NameValue } from "../../sharedTypes";
import { formatBytes } from "../../helpers";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const userStore = useUserStore();

var user = computed(() => userStore.user);

const getDocDownloadPath = (doc: DocumentDTO) =>
  `/api/property/document/${doc.documentId}/download/${doc.fileName}`;
const getDocDownloadPathWithToken = (doc: DocumentDTO) =>
  getDocDownloadPath(doc) + `?token=${user?.value.token}`;

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var docs = ref(null as DocumentDTO[]);

onMounted(loadData);
watch(property, loadData);

function loadData() {
  // Assign fresh sortOrder values based off the current order (in case of unmigrated)
  let newSortValue = 0;
  docs.value = bldgStore.property?.documents?.map((doc) => {
    return {
      ...doc,
      sortOrder: newSortValue++,
    };
  });
}

// Popup stuff
let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    documentLabel: yup.string().required().max(255).label("Label"),
    documentFileName: yup.string().required().max(255).label("File Name"),
  }),
  initialValues: {
    documentLabel: "",
    documentFileName: "",
  },
});

let documentId = ref(0);
const { value: documentLabel } = useField<string>("documentLabel");
const { value: documentFileName } = useField<string>("documentFileName");
let documentOldFileName = "";
let documentMimeType = "";
let file = ref<File>();
let documentAvailableOffline = ref(true);
let documentAllowAnonLinkView = ref(false);
let documentAllowAnonDownload = ref(false);
let documentRoleOptions = computed(() =>
  property.value.siteRoles.map(
    (r) =>
      ({
        name: r.roleAlias + (r.isStaffRole ? " (Staff)" : ""),
        value: r.siteRoleId,
      } as NameValue)
  )
);
let documentSelectedRoleOptions = ref([] as number[]);
let documentSortOrder = ref(-1);
let documentFileSize = ref(0);

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  console.log("Saving document");
  if (!property.value) {
    return;
  }

  if (!isEditing.value && !file.value) {
    saveError.value = "You must specify a file to upload.";
    return;
  }

  // Check for invalid chars
  const invalidChars = '\\/:*?"<>|';
  const validFilename = [...invalidChars].every(
    (c) => documentFileName.value.indexOf(c) < 0
  );
  if (!validFilename) {
    saveError.value = `Filename cannot include any of the following characters: ${invalidChars}`;
    return;
  }

  for (let doc of docs.value) {
    if (
      documentId.value != doc.documentId &&
      doc.fileName.normalize() === documentFileName.value.normalize()
    ) {
      saveError.value = "Filename already exists.";
      return;
    }
  }

  let saveDoc = {
    documentId: documentId.value,
    portalId: property.value?.portalId,
    label: documentLabel.value,
    fileName: documentFileName.value,
    mimeType: file.value?.type ?? documentMimeType,
    allowAnonLinkView: documentAllowAnonLinkView.value,
    allowAnonDownload: documentAllowAnonDownload.value,
    siteRoleIDs: documentSelectedRoleOptions.value,
    sortOrder: documentSortOrder.value,
    fileSize: file.value?.size ?? documentFileSize.value,
    isAvailableOffline: documentAvailableOffline.value,
  } as DocumentDTO;

  saveError.value = "";

  console.log("Saving document", saveDoc);
  if (property.value && property.value.portalId) {
    saveError.value = await bldgStore.saveDoc(saveDoc); // Save document to DB
    if (!saveError.value) {
      if (!isEditing) {
        // Save new doc
        let fileParameter = { data: file.value, fileName: documentFileName.value };
        saveError.value = await bldgStore.uploadDoc(
          fileParameter,
          property.value.siteCode
        );
      } else {
        // Editing existing doc
        if (file.value) {
          // Upload new doc in place of old one
          let fileParameter = { data: file.value, fileName: documentFileName.value };
          saveError.value = await bldgStore.uploadDoc(
            fileParameter,
            property.value.siteCode,
            documentOldFileName
          );
        } else if (
          documentOldFileName.normalize() !== documentFileName.value.normalize()
        ) {
          // Rename doc in-place
          saveError.value = await bldgStore.renameDoc(
            property.value.siteCode,
            documentOldFileName,
            documentFileName.value
          );
        }
      }
      if (!saveError.value) {
        await bldgStore.tryGetBuildingData(bldgStore.bldgCode, true);
        isPopupVisible.value = false;
        file.value = undefined;
      }
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("Error trying to submit form", ctx);
}

let addClick = function (top: boolean) {
  documentId.value = 0;
  documentLabel.value = "";
  documentFileName.value = "";
  documentOldFileName = undefined;
  file.value = undefined;
  documentAvailableOffline.value = true;
  documentAllowAnonLinkView.value = false;
  documentAllowAnonDownload.value = false;
  documentSelectedRoleOptions.value = documentRoleOptions.value.map(
    (o) => o.value as number
  );

  if (top) {
    documentSortOrder.value = Math.min(...docs.value.map((d) => d.sortOrder)) - 1;
  } else {
    documentSortOrder.value = Math.max(...docs.value.map((d) => d.sortOrder)) + 1;
  }

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selected: DocumentDTO) {
  documentId.value = selected.documentId ?? 0;
  documentLabel.value = selected.label ?? "";
  documentFileName.value = selected.fileName ?? "";
  documentOldFileName = documentFileName.value;
  documentMimeType = selected.mimeType;
  file.value = undefined;
  documentAvailableOffline.value = selected.isAvailableOffline;
  documentAllowAnonLinkView.value = selected.allowAnonLinkView;
  documentAllowAnonDownload.value = selected.allowAnonDownload;
  documentSelectedRoleOptions.value = selected.siteRoles.map((r) => r.siteRoleId);
  documentSortOrder.value = selected.sortOrder;
  documentFileSize.value = selected.fileSize;

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && documentId.value > 0) {
    if (await bldgStore.deleteDoc(documentId.value)) {
      console.log(`Successfully deleted document ${documentId.value}`);
      isPopupVisible.value = false;
      await bldgStore.tryGetBuildingData(bldgStore.bldgCode, true);
    }
  }
}

function fileChange(event: Event) {
  let filepath = (event.target as HTMLInputElement).value;
  file.value = (document.querySelector("[type=file]") as HTMLInputElement).files[0];
  documentFileName.value = filepath.substring(filepath.lastIndexOf("\\") + 1);
}

var docOrderChanged = ref(false);

function swapDocSort(sortID: number, sortUp: boolean) {
  let selectedDocID = docs.value.find((doc) => doc.sortOrder == sortID).documentId;

  if (sortUp) {
    let swapDocID = docs.value.find((doc) => doc.sortOrder == sortID - 1).documentId;
    docs.value.find((doc) => doc.documentId == selectedDocID).sortOrder = sortID - 1;
    docs.value.find((doc) => doc.documentId == swapDocID).sortOrder = sortID;

    docOrderChanged.value = true;
  } else {
    let swapDocID = docs.value.find((doc) => doc.sortOrder == sortID + 1).documentId;
    docs.value.find((doc) => doc.documentId == selectedDocID).sortOrder = sortID + 1;
    docs.value.find((doc) => doc.documentId == swapDocID).sortOrder = sortID;

    docOrderChanged.value = true;
  }

  sortAllDocs();
}

function sortAllDocs() {
  docs.value.sort((x, y) => {
    if (x.sortOrder < y.sortOrder) {
      return -1;
    } else if (x.sortOrder > y.sortOrder) {
      return 1;
    }
    return 0;
  });
}

async function saveSortedDocs() {
  let orderToSave = {
    documentOrders: docs.value.map((doc) => {
      return {
        documentId: doc.documentId,
        sortOrder: doc.sortOrder,
      };
    }),
  };

  saveError.value = await bldgStore.saveDocOrder(orderToSave);

  if (saveError.value == null) docOrderChanged.value = false;

  loadData();
}
</script>
